import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input, Select, StatusChip } from '@monigo/ui';
import { ADMIN_ROLES, type AdminRole } from '@monigo/shared';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { useSession, hasRole } from '../../lib/store/session.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { ResponsiveTable } from '../../components/Table/ResponsiveTable.js';
import { NameCell } from './NameCell.js';

interface StaffRow {
  id: string;
  role: AdminRole;
  status: string;
  display_name: string | null;
  email: string | null;
  created_at: string;
}

export function StaffPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { role: myRole } = useSession();
  const canManage = hasRole(myRole, ['super_admin']);

  const staffQ = useQuery({
    queryKey: qk.staff,
    queryFn: () => callFunction<{ items: StaffRow[] }>('admin-staff-list'),
  });

  const invite = useMutation({
    mutationFn: (input: { email: string; role: AdminRole; display_name?: string }) =>
      callFunction('admin-staff-invite', { body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  });

  const updateStaff = useMutation({
    mutationFn: (input: { user_id: string; role: AdminRole; display_name?: string | null }) =>
      callFunction('admin-staff-update-role', { body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  });

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AdminRole>('operator');

  if (staffQ.isLoading) return <LoadingScreen />;
  if (staffQ.isError) return <ErrorState onRetry={() => staffQ.refetch()} />;

  const items = staffQ.data?.items ?? [];

  const columns = [
    {
      key: 'name',
      label: t('staff.name'),
      hideOnMobile: true,
      render: (s: StaffRow) => (
        <NameCell
          name={s.display_name}
          email={s.email ?? s.id.slice(0, 8)}
          canEdit={canManage}
          onSave={(name) => {
            const clean = name.trim();
            updateStaff.mutate({
              user_id: s.id,
              role: s.role,
              ...(clean ? { display_name: clean } : { display_name: null }),
            });
          }}
        />
      ),
    },
    {
      key: 'role',
      label: t('staff.role'),
      render: (s: StaffRow) =>
        canManage ? (
          <Select
            value={s.role}
            onChange={(v) => updateStaff.mutate({ user_id: s.id, role: v as AdminRole })}
            className="h-9"
            options={ADMIN_ROLES.map((r) => ({ value: r, label: r }))}
          />
        ) : (
          <span>{s.role}</span>
        ),
    },
    {
      key: 'status',
      label: t('staff.status'),
      render: (s: StaffRow) => <StatusChip status={s.status} />,
    },
    {
      key: 'since',
      label: t('staff.since'),
      render: (s: StaffRow) => (
        <span className="text-text-muted">
          {new Date(s.created_at).toLocaleDateString(
            i18n.language === 'fr' ? 'fr-FR' : 'en-US',
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('staff.inviteCta')}</CardTitle>
          </CardHeader>
          <form
            className="flex flex-col gap-3 md:flex-row md:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (!email.trim()) return;
              invite.mutate(
                { email: email.trim(), role, display_name: displayName.trim() || undefined },
                {
                  onSuccess: () => {
                    setEmail('');
                    setDisplayName('');
                  },
                },
              );
            }}
          >
            <div className="flex-1 space-y-1">
              <label className="text-xs font-semibold text-text-muted md:hidden">{t('staff.inviteEmail')}</label>
              <Input
                type="email"
                placeholder={t('staff.inviteEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-xs font-semibold text-text-muted md:hidden">{t('staff.name')}</label>
              <Input
                placeholder={t('staff.name')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-xs font-semibold text-text-muted md:hidden">{t('staff.role')}</label>
              <Select
                value={role}
                onChange={(v) => setRole(v as AdminRole)}
                options={ADMIN_ROLES.map((r) => ({ value: r, label: r }))}
              />
            </div>
            <Button type="submit" isLoading={invite.isPending} className="mt-2 md:mt-0">
              {t('staff.inviteCta')}
            </Button>
          </form>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('staff.title')}</CardTitle>
        </CardHeader>
        <div className="px-5 pb-5">
          <ResponsiveTable
            columns={columns}
            data={items}
            keyExtractor={(s) => s.id}
            renderMobileHeader={(s) => (
              <div className="flex flex-col">
                <span className="font-semibold text-text">
                  {s.display_name ?? s.email ?? s.id.slice(0, 8)}
                </span>
                {s.display_name && s.email ? (
                  <span className="text-xs text-text-muted">{s.email}</span>
                ) : null}
              </div>
            )}
            emptyMessage={t('staff.empty')}
          />
        </div>
      </Card>
    </div>
  );
}

